import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BADGES } from "@/lib/categories";
import TipCard from "@/components/TipCard";
import { getAvatarUrl, initials, formatDate } from "@/lib/helpers";
import { Camera, Save, Loader2, Heart, MapPin, Calendar, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Profile() {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", zip_code: "" });
  const [saving, setSaving] = useState(false);
  const [myTips, setMyTips] = useState([]);
  const [loadingTips, setLoadingTips] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        zip_code: user.zip_code || "",
      });
    }
  }, [user]);

  useEffect(() => {
    // Refresh user data on mount so Tips/Likes/Badges are always current
    refresh();
    const loadTips = async () => {
      setLoadingTips(true);
      try {
        const { data } = await api.get("/users/me/tips");
        setMyTips(data.tips);
      } catch {}
      setLoadingTips(false);
    };
    loadTips();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/profile", form);
      await refresh();
      toast.success("Profile updated");
      setEditing(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not save"));
    }
    setSaving(false);
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/profile/password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password updated");
      setPwOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not change password"));
    }
    setPwSaving(false);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailSaving(true);
    try {
      await api.post("/profile/email", {
        current_password: emailForm.current_password,
        new_email: emailForm.new_email.trim(),
      });
      await refresh();
      toast.success("Email updated");
      setEmailOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not change email"));
    }
    setEmailSaving(false);
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
    }
    setUploading(false);
  };

  const earnedBadges = user?.badges || [];

  return (
    <div className="space-y-8">
      {/* Header card */}
      <div className="relative overflow-hidden rounded-3xl bg-white border border-[#1B2A4A]/8 p-6 sm:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E8621A]/8 via-transparent to-[#2A9D8F]/8" />
        <div className="relative flex flex-col sm:flex-row items-center sm:items-start gap-6">
          <div className="relative">
            <Avatar className="w-24 h-24 sm:w-28 sm:h-28 ring-4 ring-white shadow-md">
              <AvatarImage src={getAvatarUrl(user?.avatar_path)} />
              <AvatarFallback className="bg-[#2A9D8F] text-white text-2xl font-bold">
                {initials(user?.first_name, user?.last_name)}
              </AvatarFallback>
            </Avatar>
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="upload-avatar-btn"
              disabled={uploading}
              className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-[#E8621A] hover:bg-[#D05615] text-white flex items-center justify-center shadow-md transition-colors duration-200"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" data-testid="avatar-file-input" />
          </div>

          <div className="flex-1 text-center sm:text-left">
            {!editing ? (
              <>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B2A4A]">
                  {user?.first_name} {user?.last_name}
                </h1>
                <div className="mt-2 flex flex-wrap gap-3 items-center justify-center sm:justify-start text-sm text-[#1B2A4A]/70">
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {user?.zip_code || "—"}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Member since {formatDate(user?.member_since)}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-4 justify-center sm:justify-start">
                  <StatChip label="Tips" value={user?.tips_count || 0} />
                  <StatChip label="Likes" value={user?.likes_received || 0} />
                  <StatChip label="Zen visits" value={user?.relaxation_visits || 0} />
                </div>
                <Button onClick={() => setEditing(true)} data-testid="edit-profile-btn" className="mt-4 rounded-full bg-[#1B2A4A] text-white hover:bg-[#1B2A4A]/90">
                  Edit profile
                </Button>
              </>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-[#1B2A4A]/70">First name</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="edit-firstname" className="h-11 rounded-xl bg-white border-[#1B2A4A]/15" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-[#1B2A4A]/70">Last name</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    data-testid="edit-lastname" className="h-11 rounded-xl bg-white border-[#1B2A4A]/15" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-[#1B2A4A]/70">Zip code</Label>
                  <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                    data-testid="edit-zip" className="h-11 rounded-xl bg-white border-[#1B2A4A]/15" />
                </div>
                <div className="sm:col-span-3 flex flex-wrap justify-between gap-2 pt-1">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setPwForm({ current_password: "", new_password: "", confirm_password: "" }); setPwOpen(true); }}
                      data-testid="change-password-btn"
                      className="rounded-full border-[#1B2A4A]/15 text-[#1B2A4A] hover:bg-[#1B2A4A]/5"
                    >
                      <KeyRound className="w-4 h-4 mr-2" /> Change password
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setEmailForm({ current_password: "", new_email: "" }); setEmailOpen(true); }}
                      data-testid="change-email-btn"
                      className="rounded-full border-[#1B2A4A]/15 text-[#1B2A4A] hover:bg-[#1B2A4A]/5"
                    >
                      <Mail className="w-4 h-4 mr-2" /> Change email
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-full" data-testid="edit-cancel">Cancel</Button>
                    <Button onClick={save} disabled={saving} data-testid="edit-save" className="rounded-full bg-[#E8621A] hover:bg-[#D05615]">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Badges */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-4 h-4 text-[#E8621A]" />
          <h2 className="font-heading font-bold text-xl text-[#1B2A4A]">Badges</h2>
          <span className="text-sm text-[#1B2A4A]/50">({earnedBadges.length}/7)</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(BADGES).map(([key, b]) => {
            const earned = earnedBadges.includes(key);
            return (
              <div
                key={key}
                data-testid={`badge-${key}`}
                className={`rounded-3xl border p-4 text-center transition-colors duration-200 ${
                  earned ? "bg-white border-[#E8621A]/30" : "bg-white/40 border-dashed border-[#1B2A4A]/15 opacity-55"
                }`}
              >
                <div className="text-3xl">{b.icon}</div>
                <p className="mt-2 font-heading font-bold text-sm text-[#1B2A4A]">{b.label}</p>
                <p className="mt-1 text-[11px] text-[#1B2A4A]/60 leading-snug">{b.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* My tips */}
      <Tabs defaultValue="tips">
        <TabsList className="rounded-full bg-white border border-[#1B2A4A]/8 p-1 h-11">
          <TabsTrigger value="tips" data-testid="tab-my-tips" className="rounded-full data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white px-4">
            My Tips ({myTips.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="tips" className="mt-4 space-y-4">
          {loadingTips ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#E8621A]" /></div>
          ) : myTips.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-3xl border border-dashed border-[#1B2A4A]/15">
              <p className="text-[#1B2A4A]/60">You haven't shared any tips yet.</p>
            </div>
          ) : (
            myTips.map((t) => <TipCard key={t.id} tip={t} />)
          )}
        </TabsContent>
      </Tabs>

      {/* Change password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="password-dialog">
          <DialogHeader>
            <div className="w-11 h-11 rounded-2xl bg-[#E8621A]/12 flex items-center justify-center mb-2">
              <KeyRound className="w-5 h-5 text-[#E8621A]" />
            </div>
            <DialogTitle className="font-heading text-2xl text-[#1B2A4A]">Change password</DialogTitle>
            <DialogDescription className="text-[#1B2A4A]/70">
              Enter your current password, then your new one twice.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPassword} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#1B2A4A]">Current password</Label>
              <Input
                type="password" required value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                data-testid="pw-current"
                className="h-11 rounded-xl bg-white border-[#1B2A4A]/15"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#1B2A4A]">New password</Label>
              <Input
                type="password" required value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                data-testid="pw-new"
                placeholder="At least 6 characters"
                className="h-11 rounded-xl bg-white border-[#1B2A4A]/15"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#1B2A4A]">Confirm new password</Label>
              <Input
                type="password" required value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                data-testid="pw-confirm"
                className="h-11 rounded-xl bg-white border-[#1B2A4A]/15"
              />
            </div>
            <DialogFooter className="flex-row justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setPwOpen(false)} className="rounded-full" data-testid="pw-cancel">Cancel</Button>
              <Button type="submit" disabled={pwSaving} data-testid="pw-submit" className="rounded-full bg-[#E8621A] hover:bg-[#D05615]">
                {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="email-dialog">
          <DialogHeader>
            <div className="w-11 h-11 rounded-2xl bg-[#2A9D8F]/12 flex items-center justify-center mb-2">
              <Mail className="w-5 h-5 text-[#2A9D8F]" />
            </div>
            <DialogTitle className="font-heading text-2xl text-[#1B2A4A]">Change email</DialogTitle>
            <DialogDescription className="text-[#1B2A4A]/70">
              Current email: <span className="font-semibold text-[#1B2A4A]">{user?.email}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEmail} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#1B2A4A]">Current password</Label>
              <Input
                type="password" required value={emailForm.current_password}
                onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })}
                data-testid="email-current-pw"
                className="h-11 rounded-xl bg-white border-[#1B2A4A]/15"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#1B2A4A]">New email</Label>
              <Input
                type="email" required value={emailForm.new_email}
                onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                data-testid="email-new"
                placeholder="you@example.com"
                className="h-11 rounded-xl bg-white border-[#1B2A4A]/15"
              />
            </div>
            <DialogFooter className="flex-row justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setEmailOpen(false)} className="rounded-full" data-testid="email-cancel">Cancel</Button>
              <Button type="submit" disabled={emailSaving} data-testid="email-submit" className="rounded-full bg-[#2A9D8F] hover:bg-[#218276] text-white">
                {emailSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="text-center">
      <p className="font-heading text-2xl font-bold text-[#1B2A4A]">{value}</p>
      <p className="text-[11px] uppercase tracking-widest text-[#1B2A4A]/50 font-semibold">{label}</p>
    </div>
  );
}
